import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useAutosave } from '../../../../hooks/useAutosave';
import { BlockEditor } from './BlockEditor';
import { LessonContentVersionPanel } from './LessonContentVersionPanel';
import { BlockLessonPlayer } from '../../../CoursePlayer/players/blocks/BlockLessonPlayer';
import { COMPLETION_RULES, type Block, type BlockLessonContent, type CompletionRule } from './types';

export interface BlockLessonEditorLesson {
    id: number;
    title: string;
    type: string;
    minScore?: number | null;
    content?: BlockLessonContent | null;
}

interface Props {
    lesson: BlockLessonEditorLesson;
    /** Called after every successful save with the persisted lesson. */
    onSaved?: (lesson: BlockLessonEditorLesson) => void;
}

const normalise = (content: BlockLessonContent | null | undefined): BlockLessonContent => ({
    schemaVersion: 1,
    completionRule: content?.completionRule ?? 'view_only',
    blocks: Array.isArray(content?.blocks) ? content!.blocks : [],
});

/**
 * Editor host for one block lesson: BlockEditor + completion rule + min score +
 * autosave (2 s) + Ctrl/Cmd+S + version history. Used by the course builder and
 * by the classic `/lesson/:id/content` route for `type = blocks`.
 */
export const BlockLessonEditor: React.FC<Props> = ({ lesson, onSaved }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [content, setContent] = useState<BlockLessonContent>(() => normalise(lesson.content));
    const [minScore, setMinScore] = useState<number | null>(lesson.minScore ?? null);
    const [editorKey, setEditorKey] = useState(0);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [versionsRefresh, setVersionsRefresh] = useState(0);
    const [preview, setPreview] = useState(false);

    // Reload when another lesson is selected.
    useEffect(() => {
        setContent(normalise(lesson.content));
        setMinScore(lesson.minScore ?? null);
        setEditorKey((k) => k + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lesson.id]);

    const payload = useMemo(() => ({ content, minScore }), [content, minScore]);

    const save = useCallback(
        async (data: { content: BlockLessonContent; minScore: number | null }) => {
            const { data: saved } = await dataProvider.update('lessons', {
                id: lesson.id,
                data: { content: data.content, minScore: data.minScore },
                previousData: lesson,
            });
            setVersionsRefresh((n) => n + 1);
            onSaved?.(saved as BlockLessonEditorLesson);
        },
        [dataProvider, lesson, onSaved],
    );

    const { status, lastSavedAt, saveNow, isDirty } = useAutosave({ data: payload, onSave: save, delay: 2000 });

    useEffect(() => {
        if (status === 'error') notify('blockLesson.saveError', { type: 'error' });
    }, [status, notify]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                void saveNow();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [saveNow]);

    const onBlocksUpdate = useCallback((blocks: Block[]) => {
        setContent((prev) => ({ ...prev, blocks }));
    }, []);

    const statusLabel: Record<string, string> = {
        idle: translate('blockLesson.status.idle'),
        unsaved: translate('blockLesson.status.unsaved'),
        saving: translate('blockLesson.status.saving'),
        saved: lastSavedAt ? translate('blockLesson.status.savedAt', { time: lastSavedAt.toLocaleTimeString() }) : translate('blockLesson.status.saved'),
        error: translate('blockLesson.status.error'),
    };
    const statusColor = status === 'error' ? 'error' : status === 'unsaved' || status === 'saving' ? 'warning' : 'default';

    return (
        <Box className="oa-block-lesson-editor">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="completion-rule-label">{translate('blockLesson.completionRule.label')}</InputLabel>
                    <Select
                        labelId="completion-rule-label"
                        label={translate('blockLesson.completionRule.label')}
                        value={content.completionRule ?? 'view_only'}
                        onChange={(e) => setContent((prev) => ({ ...prev, completionRule: e.target.value as CompletionRule }))}
                    >
                        {COMPLETION_RULES.map((rule) => (
                            <MenuItem key={rule} value={rule}>{translate(`blockLesson.completionRule.${rule}`)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {content.completionRule === 'quiz_passed' && (
                    <TextField
                        size="small"
                        type="number"
                        label={translate('blockLesson.minScore')}
                        value={minScore ?? ''}
                        onChange={(e) => setMinScore(e.target.value === '' ? null : Math.max(0, Math.min(100, Number(e.target.value))))}
                        inputProps={{ min: 0, max: 100 }}
                        helperText={translate('blockLesson.minScoreHint')}
                        sx={{ width: 160 }}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title={translate('blockLesson.completionRule.hint')}>
                    <Chip size="small" color={statusColor} label={statusLabel[status]} />
                </Tooltip>
                <Button size="small" variant="outlined" startIcon={<SaveOutlined />} disabled={!isDirty || status === 'saving'} onClick={() => void saveNow()}>
                    {translate('ra.action.save')}
                </Button>
                <Button size="small" startIcon={<HistoryOutlined />} onClick={() => setVersionsOpen(true)}>
                    {translate('blockLesson.versions.title')}
                </Button>
                <Button size="small" variant={preview ? 'contained' : 'outlined'} startIcon={preview ? <EditOutlined /> : <VisibilityOutlined />} onClick={() => setPreview((p) => !p)} aria-pressed={preview}>
                    {translate(preview ? 'blockLesson.preview.edit' : 'blockLesson.preview.show')}
                </Button>
            </Stack>

            {preview ? (
                // Echte Lernenden-Ansicht (read-only: nichts wird gewertet), plan §6.7.
                <Box className="oa-block-lesson-preview">
                    <BlockLessonPlayer lesson={{ id: lesson.id, title: lesson.title, content, minScore }} readOnly />
                </Box>
            ) : (
                <BlockEditor key={editorKey} blocks={content.blocks} onUpdate={onBlocksUpdate} />
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {translate('blockLesson.shortcutsHint')}
            </Typography>

            <LessonContentVersionPanel
                lessonId={lesson.id}
                open={versionsOpen}
                onClose={() => setVersionsOpen(false)}
                refreshKey={versionsRefresh}
                onRestored={(restored) => {
                    setContent(normalise(restored));
                    setEditorKey((k) => k + 1);
                }}
            />
        </Box>
    );
};

export default BlockLessonEditor;
